import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SplitLines } from "../components/motion/SplitLines";
import { Reveal, EASE } from "../components/motion/Reveal";
import { QuestionList } from "../components/shared/QuestionList";
import { GoDeeper } from "../components/laws/GoDeeper";
import { StudyRail } from "../components/laws/StudyRail";
import { LAWS, getLawBySlug } from "../data/laws";

const TOC = [
  { id: "source", label: "Source in Torah" },
  { id: "meaning", label: "What it means" },
  { id: "daily-life", label: "In daily life" },
  { id: "questions", label: "Common questions" },
  { id: "deeper", label: "Go deeper" },
];

const Sidebar = ({ law }) => (
  <aside className="no-scrollbar lg:sticky lg:top-32 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto">
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
    >
      <Link
        to="/"
        data-testid="law-detail-back"
        className="link-underline inline-flex items-center gap-2 text-xs font-medium text-ink-soft"
      >
        <ArrowLeft size={14} /> All seven
      </Link>
      <p className="mt-6 font-serif text-7xl leading-none text-terracotta">0{law.number}</p>
      <p className="mt-4 text-left font-serif text-3xl text-ink" dir="rtl">
        {law.hebrewScript}
      </p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
        {law.hebrew} · {law.kind}
      </p>

      <nav className="mt-12 hidden lg:block" aria-label="On this page">
        <p className="eyebrow">On this page</p>
        <ul className="mt-4 space-y-2.5">
          {TOC.map((t) => (
            <li key={t.id}>
              <a
                href={`#${t.id}`}
                data-testid={`law-toc-${t.id}`}
                className="link-underline text-sm text-ink-soft hover:text-ink"
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <StudyRail slug={law.slug} />
    </motion.div>
  </aside>
);

const SectionLabel = ({ id, children }) => (
  <p id={id} className="eyebrow scroll-mt-32">
    {children}
  </p>
);

const PrevNext = ({ law }) => {
  const idx = LAWS.findIndex((l) => l.slug === law.slug);
  const prev = LAWS[(idx - 1 + LAWS.length) % LAWS.length];
  const next = LAWS[(idx + 1) % LAWS.length];
  const Card = ({ target, dir }) => (
    <Link
      to={`/laws/${target.slug}`}
      data-testid={`law-nav-${dir}`}
      className={`group flex flex-col gap-4 border hairline bg-white p-8 transition-colors duration-500 hover:bg-parchment-deep ${
        dir === "next" ? "items-end text-right" : ""
      }`}
    >
      <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
        {dir === "prev" && <ArrowLeft size={12} className="transition-transform group-hover:-translate-x-1" />}
        {dir === "prev" ? "Previous" : "Next"}
        {dir === "next" && <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />}
      </span>
      <span className="font-serif text-3xl leading-tight text-ink">
        <span className="mr-3 text-terracotta">0{target.number}</span>
        {target.title}
      </span>
    </Link>
  );
  return (
    <div className="mt-24 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Card target={prev} dir="prev" />
      <Card target={next} dir="next" />
    </div>
  );
};

const ReadingProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      data-testid="reading-progress-bar"
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left bg-terracotta"
      aria-hidden="true"
    />
  );
};

export default function LawDetail() {
  const { slug } = useParams();
  const law = getLawBySlug(slug);
  if (!law) return <Navigate to="/" replace />;

  const titleLines = law.fullTitle.split(" of ");
  const lines =
    titleLines.length > 1 ? [`${titleLines[0]} of`, titleLines.slice(1).join(" of ")] : [law.fullTitle];

  return (
    <article data-testid={`law-detail-${law.slug}`} className="mx-auto max-w-7xl px-5 pt-32 pb-28 sm:px-8 lg:pt-40">
      <ReadingProgress />
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <Sidebar law={law} />
        </div>

        <div className="lg:col-span-8 lg:col-start-5">
          <SplitLines
            lines={lines}
            delay={0.15}
            className="font-serif text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
            className="mt-8 max-w-2xl font-serif text-2xl italic leading-snug text-ink-soft"
          >
            {law.statement}
          </motion.p>

          <Reveal className="mt-20">
            <SectionLabel id="source">Source in Torah</SectionLabel>
            <blockquote className="mt-6 border-l-2 border-terracotta pl-6 sm:pl-8">
              <p className="font-serif text-2xl leading-snug text-ink sm:text-3xl">“{law.source.text}”</p>
              <cite className="mt-4 block font-mono text-[11px] not-italic uppercase tracking-[0.22em] text-ink-muted">
                {law.source.ref}
              </cite>
            </blockquote>
            <ul className="mt-6 space-y-1.5 text-sm text-ink-muted">
              {law.secondarySources.map((s) => (
                <li key={s} className="flex gap-3">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-terracotta" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-20">
            <SectionLabel id="meaning">What it means</SectionLabel>
            <div className="prose-warm mt-6 max-w-2xl text-base leading-[1.8] text-ink-soft sm:text-lg">
              {law.meaning.map((p, i) => (
                <p key={i} className={i === 0 ? "drop-cap" : ""}>
                  {p}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-20">
            <SectionLabel id="daily-life">In daily life</SectionLabel>
            <ol className="mt-6 grid grid-cols-1 gap-px bg-ink/10 sm:grid-cols-2">
              {law.dailyLife.map((item, i) => (
                <li
                  key={i}
                  data-testid={`law-daily-life-item-${i + 1}`}
                  className="flex gap-5 bg-parchment p-6 sm:p-7"
                >
                  <span className="font-serif text-3xl leading-none text-terracotta">{i + 1}</span>
                  <p className="text-sm leading-relaxed text-ink-soft sm:text-base">{item}</p>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal className="mt-20">
            <SectionLabel id="questions">Common questions</SectionLabel>
            <QuestionList items={law.faqs} idPrefix="law-faq" className="mt-6" />
          </Reveal>

          <GoDeeper law={law} />

          <PrevNext law={law} />
        </div>
      </div>
    </article>
  );
}
